import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError, fileUrl } from "@/lib/api";
import { formatDate } from "@/components/public/Shared";

function FieldInput({ field, value, onChange, testId }) {
  if (field.type === "textarea") {
    return <Textarea id={testId} data-testid={testId} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === "checkbox") {
    return (
      <div className="flex items-center gap-2 pt-1">
        <Checkbox id={testId} data-testid={testId} checked={!!value} onCheckedChange={(v) => onChange(!!v)} />
        <Label htmlFor={testId} className="text-sm font-normal text-foreground/80">{field.label}</Label>
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "image") {
    return <ImageUpload value={value || ""} onChange={onChange} testId={testId} />;
  }
  return (
    <Input
      id={testId}
      data-testid={testId}
      type={field.type || "text"}
      value={value ?? ""}
      placeholder={field.placeholder || ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
    />
  );
}

export default function CrudPage({ title, subtitle, apiPath, columns, fields, addLabel, searchPlaceholder, testId }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    api.get(apiPath, { params }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  }, [apiPath, q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => {
      if (f.default !== undefined) init[f.key] = f.default;
      else if (f.type === "checkbox") init[f.key] = false;
      else init[f.key] = "";
    });
    setForm(init);
    setEditing("new");
  };

  const openEdit = (item) => {
    const init = {};
    fields.forEach((f) => { init[f.key] = item[f.key] ?? (f.type === "checkbox" ? false : ""); });
    setForm(init);
    setEditing(item);
  };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !form[f.key]) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post(apiPath, form);
        toast.success(`${title.slice(0, -1)} added successfully`);
      } else {
        await api.put(`${apiPath}/${editing.id}`, form);
        toast.success("Saved successfully");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${apiPath}/${deleting.id}`);
      toast.success("Deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const cellValue = (item, col) => {
    const v = item[col.key];
    if (col.type === "date") return formatDate(v) || "—";
    if (col.type === "bool") return v ? "Yes" : "No";
    if (col.type === "status") {
      return (
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${v ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
          {v ? "Published" : "Hidden"}
        </span>
      );
    }
    if (col.type === "image") {
      return v ? <img src={fileUrl(v)} alt="" className="h-10 w-14 rounded object-cover" /> : <span className="text-xs text-muted-foreground">—</span>;
    }
    return v ?? "—";
  };

  return (
    <div data-testid={testId} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Button onClick={openCreate} className="bg-navy text-cream hover:bg-navy-light" data-testid={`${testId}-add-btn`}>
          <Plus className="mr-2 h-4 w-4" /> {addLabel}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 bg-white"
            data-testid={`${testId}-search`}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid={`${testId}-status-filter`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="true">Published</SelectItem>
            <SelectItem value="false">Hidden</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!items && <p className="text-sm text-muted-foreground">Loading…</p>}
      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid={`${testId}-empty`}>
          <p className="font-heading text-lg font-bold text-navy">Nothing here yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Click “{addLabel}” to create the first entry.</p>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
          <table className="w-full text-sm" data-testid={`${testId}-table`}>
            <thead>
              <tr className="border-b border-border bg-secondary/60 text-left">
                {columns.map((c) => (
                  <th key={c.key} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">{c.label}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-secondary/30" data-testid={`${testId}-row-${item.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="max-w-56 truncate px-4 py-3 text-foreground/85">{cellValue(item, c)}</td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`${testId}-edit-${item.id}`} aria-label="Edit">
                        <Pencil className="h-4 w-4 text-navy/60" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleting(item)} data-testid={`${testId}-delete-${item.id}`} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`${testId}-form-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">{editing === "new" ? addLabel : "Edit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.key}>
                {f.type !== "checkbox" && (
                  <Label htmlFor={`${testId}-field-${f.key}`}>
                    {f.label} {f.required && <span className="text-destructive">*</span>}
                  </Label>
                )}
                <div className={f.type === "checkbox" ? "" : "mt-1.5"}>
                  <FieldInput
                    field={f}
                    value={form[f.key]}
                    onChange={(v) => setForm({ ...form, [f.key]: v })}
                    testId={`${testId}-field-${f.key}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid={`${testId}-form-cancel`}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-light" data-testid={`${testId}-form-save`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid={`${testId}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete “{deleting?.title || deleting?.name || "this record"}”. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`${testId}-delete-confirm`}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
